import React, { useMemo, useState } from "react";
import { ArrowUpRight, Search, SlidersHorizontal, Sparkles, X } from "lucide-react";
import Particles from "../../Background/Particles";
import manualArticlesRaw from "../../data/articles.json";
import GitHubActivity from "./GitHubActivity";
import Seo from "../../components/Seo";

type Platform = "reddit" | "linkedin" | "x-twitter";

interface ArtikelItem {
  platform: Platform;
  icon: string;
  color: string;
  title: string;
  desc: string;
  url: string;
  date: string;
}

const articles = manualArticlesRaw as ArtikelItem[];

const platformLabels: Record<Platform, string> = {
  linkedin: "LinkedIn",
  "x-twitter": "X / Twitter",
  reddit: "Reddit",
};

const platformFilters: Array<{ label: string; value: Platform | "all" }> = [
  { label: "All", value: "all" },
  { label: "LinkedIn", value: "linkedin" },
  { label: "X / Twitter", value: "x-twitter" },
  { label: "Reddit", value: "reddit" },
];

const sortByNewest = (items: ArtikelItem[]) =>
  [...items].sort((first, second) => new Date(second.date).getTime() - new Date(first.date).getTime());

const Artikel: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activePlatform, setActivePlatform] = useState<Platform | "all">("all");

  const sortedArticles = useMemo(() => sortByNewest(articles), []);

  const filteredArticles = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase("id-ID");

    return sortedArticles.filter((article) => {
      const matchesPlatform = activePlatform === "all" || article.platform === activePlatform;
      const matchesSearch =
        !query ||
        article.title.toLocaleLowerCase("id-ID").includes(query) ||
        article.desc.toLocaleLowerCase("id-ID").includes(query) ||
        platformLabels[article.platform].toLocaleLowerCase("id-ID").includes(query);

      return matchesPlatform && matchesSearch;
    });
  }, [activePlatform, searchTerm, sortedArticles]);

  const latestArticles = useMemo(() => sortedArticles.slice(0, 3), [sortedArticles]);
  const hasActiveFilter = searchTerm.length > 0 || activePlatform !== "all";

  const resetFilters = () => {
    setSearchTerm("");
    setActivePlatform("all");
  };

  return (
    <>
      <Seo
        title="Articles & Insights — Efwan Rizaldi"
        description="A collection of articles, learning notes, and technology discussions by Efwan Rizaldi shared across LinkedIn, X and Reddit."
        path="/artikel"
        type="article"
      />
      <section
      id="artikel"
      className="relative isolate min-h-screen overflow-hidden bg-[#070b16] px-4 pb-16 pt-32 text-white sm:px-6 sm:pb-20 sm:pt-36 lg:px-8 lg:pb-24"
    >
      <div className="pointer-events-none absolute inset-0 -z-20 bg-[radial-gradient(circle_at_15%_10%,rgba(249,115,22,0.18),transparent_30%),radial-gradient(circle_at_85%_18%,rgba(34,211,238,0.13),transparent_27%),linear-gradient(180deg,#0b1020_0%,#070b16_46%,#090b14_100%)]" />
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-30">
        <Particles
          particleColors={["#ffffff", "#67e8f9", "#fdba74"]}
          particleCount={90}
          particleSpread={9}
          speed={0.08}
          particleBaseSize={75}
          moveParticlesOnHover
          alphaParticles
          disableRotation={false}
          pixelRatio={1}
        />
      </div>
      <div className="mx-auto w-full max-w-[1400px]">
        <div className="mx-auto max-w-[920px] text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-orange-300/20 bg-orange-400/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-orange-200 shadow-[0_0_34px_rgba(249,115,22,0.1)] sm:mb-5">
              <Sparkles size={15} aria-hidden="true" />
              Note and perspective
            </div>
            <h1 className="bg-gradient-to-r from-white via-orange-100 to-cyan-200 bg-clip-text text-[clamp(42px,5.4vw,100px)] font-black leading-[0.95] tracking-[-0.06em] text-transparent">
              Article & Insight
            </h1>
            <p className="mx-auto mt-5 max-w-[760px] text-[16px] leading-7 text-slate-300 sm:text-[18px] sm:leading-8">
              A collection of articles, learning notes, and discussions about technology that I share across various communities.
            </p>
        </div>

        <div className="mt-8 sm:mt-10"><GitHubActivity /></div>

        <div className="mt-5 rounded-[28px] border border-white/10 bg-slate-950/55 p-[14px] shadow-[0_24px_80px_rgba(0,0,0,0.28)] backdrop-blur-xl sm:mt-6 sm:p-[18px]">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative min-w-0 flex-1 lg:max-w-[610px]">
                <label className="sr-only" htmlFor="article-search">
                  Search articles
                </label>
                <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={19} aria-hidden="true" />
                <input
                  id="article-search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") setSearchTerm("");
                  }}
                  autoComplete="off"
                  placeholder="Search topics, technologies, or platforms..."
                  className="w-full rounded-2xl border border-white/10 bg-white/[0.055] py-[15px] pl-12 pr-11 text-[15px] text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-300/60 focus:bg-white/[0.08] focus:ring-4 focus:ring-cyan-400/10"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-slate-400 transition hover:bg-white/10 hover:text-white"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <div className="flex min-w-0 items-center gap-2 overflow-x-auto pb-1 lg:justify-end lg:pb-0">
                <SlidersHorizontal className="ml-1 shrink-0 text-slate-500" size={17} aria-hidden="true" />
                {platformFilters.map((filter) => {
                  const isActive = activePlatform === filter.value;
                  return (
                    <button
                      key={filter.value}
                      type="button"
                      onClick={() => setActivePlatform(filter.value)}
                      aria-pressed={isActive}
                      className={`shrink-0 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition sm:px-4 ${
                        isActive
                          ? "bg-gradient-to-r from-orange-500 to-amber-400 text-slate-950 shadow-[0_8px_24px_rgba(249,115,22,0.25)]"
                          : "border border-white/10 bg-white/[0.045] text-slate-300 hover:border-cyan-300/30 hover:bg-cyan-300/10 hover:text-cyan-100"
                      }`}
                    >
                      {filter.label}
                    </button>
                  );
                })}
              </div>
            </div>
        </div>

        <div className="mt-8 grid gap-[26px] xl:grid-cols-[minmax(0,1fr)_340px] xl:items-start">
          <div>
            <div className="mb-4 flex items-center justify-between gap-4 px-1">
              <p aria-live="polite" className="text-[15px] text-slate-400">
                {searchTerm ? (
                  <>search <span className="font-semibold text-cyan-200">“{searchTerm}”</span> · </>
                ) : null}
                showing <span className="font-bold text-white">{filteredArticles.length}</span> article
              </p>
              {hasActiveFilter && (
                <button type="button" onClick={resetFilters} className="text-sm font-semibold text-cyan-300 transition hover:text-cyan-100">
                  Reset filter
                </button>
              )}
            </div>

            {filteredArticles.length ? (
              <div className="grid gap-[18px] sm:gap-[22px]">
                {filteredArticles.map((article) => (
                  <article key={article.url} className="group relative overflow-hidden rounded-[28px] border border-orange-300/25 bg-slate-950/60 p-[24px] shadow-[0_18px_55px_rgba(0,0,0,0.22)] transition duration-300 hover:-translate-y-1 hover:border-cyan-300/30 hover:shadow-[0_24px_65px_rgba(6,182,212,0.12)] sm:p-[30px]">
                      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-200/50 to-transparent opacity-0 transition group-hover:opacity-100" />
                      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-6">
                        <div className="flex h-[58px] w-[58px] shrink-0 items-center justify-center rounded-2xl border border-orange-300/25 shadow-inner shadow-white/5 sm:h-[66px] sm:w-[66px]">
                          <i className={`fab ${article.icon} text-[28px] sm:text-[33px] ${article.color}`} aria-hidden="true" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-medium">
                            <span className="rounded-full border border-orange-300/20 bg-orange-400/10 px-2.5 py-1 font-bold uppercase tracking-[0.15em] text-orange-200">
                              {platformLabels[article.platform]}
                            </span>
                            <time className="text-slate-400">{article.date}</time>
                          </div>
                          <h2 className="mt-3 text-[22px] font-bold leading-snug tracking-[-0.02em] text-white transition group-hover:text-cyan-100 sm:text-[27px]">
                            {article.title}
                          </h2>
                          <p className="mt-3 max-w-3xl text-[15px] leading-7 text-slate-400 sm:text-[17px] sm:leading-8">{article.desc}</p>
                        </div>
                        <a
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex w-fit shrink-0 items-center gap-2 rounded-xl border border-cyan-300/20 bg-cyan-300/10 px-4 py-3 text-[15px] font-bold text-cyan-100 transition hover:border-cyan-200/60 hover:bg-cyan-300 hover:text-slate-950 focus:outline-none focus:ring-4 focus:ring-cyan-300/20 sm:mt-1"
                        >
                          Read article
                          <ArrowUpRight size={17} aria-hidden="true" />
                        </a>
                      </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="rounded-[1.6rem] border border-dashed border-white/15 bg-slate-950/45 px-6 py-16 text-center backdrop-blur-sm">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-white/[0.06] text-cyan-200"><Search size={23} /></div>
                <h2 className="mt-5 text-xl font-bold text-white">Article not found</h2>
                <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-400">Try a different keyword or show all articles again.</p>
                <button type="button" onClick={resetFilters} className="mt-6 rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-slate-950 transition hover:bg-cyan-200">Show all</button>
              </div>
            )}
          </div>

          <aside className="grid gap-[22px] sm:grid-cols-2 xl:sticky xl:top-28 xl:grid-cols-1">
            <section className="rounded-[28px] border border-white/10 bg-white/[0.045] p-[26px] shadow-[0_18px_55px_rgba(0,0,0,0.18)] backdrop-blur-xl sm:p-[30px]">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-orange-300">From different spaces</p>
              <h2 className="mt-2 text-[24px] font-bold leading-tight text-white">Stay connected with fresh ideas.</h2>
              <p className="mt-3 text-[16px] leading-7 text-slate-400">Short pieces from the professional and community channels I follow.</p>
              <div className="mt-5 flex items-center gap-3 border-t border-white/10 pt-4 text-sm text-slate-300">
                <span className="text-2xl font-black text-cyan-200">{articles.length}</span>
                <span>curated articles</span>
              </div>
            </section>

            <section className="rounded-[28px] border border-white/10 bg-slate-950/60 p-[26px] shadow-[0_18px_55px_rgba(0,0,0,0.18)] backdrop-blur-xl sm:p-[30px]">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-300">Latest picks</p>
                  <h2 className="mt-1 text-lg font-bold text-white">Don’t miss out</h2>
                </div>
                <Sparkles className="text-orange-300" size={20} aria-hidden="true" />
              </div>
              <div className="mt-4 divide-y divide-white/10">
                {latestArticles.map((article, index) => (
                  <a key={article.url} href={article.url} target="_blank" rel="noopener noreferrer" className="group/latest flex gap-3 py-4 first:pt-0 last:pb-0">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-cyan-300 text-xs font-black text-slate-950">0{index + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold leading-5 text-slate-200 transition group-hover/latest:text-cyan-200">{article.title}</span>
                      <span className="mt-1 block text-xs text-slate-500">{article.date} · {platformLabels[article.platform]}</span>
                    </span>
                  </a>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </section>
    </>
  );
};

export default Artikel;
